import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useIdentity } from '@/entities/session';
import { useConnectionNames, useConnectionSummaries } from '@/entities/connection';
import { useFindings } from '@/features/anomaly';
import { useDatasetNames } from '@/features/catalog-browser';
import { useReviewQueue } from '@/features/classification-review';
import { ageInMinutes, useIncidentQueue } from '@/features/incidents';
import { isSystemActor, useRecentActivity } from '@/features/audit';
import { useFailureWindow } from '@/features/rules';
import { ESTATE_TREND_DAYS, useEstate, useEstateTrend } from '@/features/scoring';
import { usePlanQueue } from '@/features/remediation';

import { SCREEN_PATHS } from '@/shared/routes';
import { AttentionTable } from '@/shared/ui/AttentionTable';
import type { AttentionRow } from '@/shared/ui/AttentionTable';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Section, Sections } from '@/shared/ui/Layout';
import { MetricCard } from '@/shared/ui/Metric';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SegmentBar, Sparkline } from '@/shared/ui/Spark';
import { LoadingState, UnavailableState } from '@/shared/ui/States';

import {
  countSeverities,
  describeAge,
  nameSubject,
  needsAttention,
  orderAttention,
  severityRank,
  unreadableSources,
} from '../model/overview';
import {
  UNRECOGNISED_KEY,
  activityKey,
  activitySentence,
  activityWhen,
} from '../model/activity';
import { unmeasuredBySource, unmeasuredTotal } from '../model/unmeasured';
import type { AttentionItem } from '../model/overview';
import type { UnmeasuredRow } from '../model/unmeasured';

/* The front door: is my data healthy, what needs attention, and what should I do next.
 *
 * **Every figure on this page comes from an endpoint some other screen already reads.** Six parallel
 * reads, no new API surface, and no per-dataset fan-out — which is the constraint that shaped the
 * page more than any visual decision. A dashboard is exactly where an N+1 gets written, because the
 * tempting card is "average score across your estate" and the only way to build it from today's API is
 * one request per dataset.
 *
 * ---------------------------------------------------------------------------------------------
 * **Three of the reference's four headline cards are now real, and two of them needed a new endpoint.**
 * This page carried a `BackendBadge` on its first card for months, with the note that "there is no
 * endpoint that aggregates scores across a tenant" — true at the time, and the badge was the right
 * answer to it. `GET /v1/scoring/estate` is that endpoint: one statement the database aggregates,
 * six numbers on the wire, constant in the size of the estate. The alternative the frontend had was
 * one request per dataset, which is the N+1 a dashboard is exactly where somebody writes.
 *
 * **"Datasets measured" pairs two sources deliberately.** `scored` comes from the scoring aggregate
 * and the total from the catalogue walk every other screen already does — because the scoring API
 * does not report a dataset total, and should not: `catalog.dataset` is another module's table, and a
 * scoring query that joined it would be the raw cross-module SQL this codebase records at eight
 * sites.
 *
 * **The fourth card — rule failures over 24 hours — is still not servable** and is not faked. It
 * needs an aggregate over `rules.task` that does not exist, which is a separate slice.
 *
 * **A card that could not read its source says so, and never renders a zero.** `count === null` is the
 * request-failed case and gets the words; `0` is a real answer and gets the digit. The two are
 * different facts — "nothing is waiting for you" against "we could not find out" — and collapsing them
 * is the defect this product records at four screens.
 *
 * **And the worklist reports its own blind spots.** Four sources feed the attention table; if one
 * fails, the table is *shorter* while looking complete, which reads as "less needs you" — the exact
 * inversion of what happened. `unreadableSources` names the ones that failed, above the table.
 */

/* How many rules the server's concentration figure covers.
 *
 * **A named constant here rather than a literal in the copy call, and it is a mirror of a server
 * value.** `top_rule_violations` is the sum over the three worst rules; the API does not publish the
 * three, because the number is a property of the aggregate rather than of the tenant. So the client
 * has to state it to write the sentence — and stating it once, named, beside a note saying where the
 * real value lives, is the honest version of a duplicated constant. If the server ever changes it,
 * this sentence is wrong and nothing catches it, which is exactly why it is written down here rather
 * than inlined twice.
 *
 * The alternative was publishing the count on the response. Recorded as the better fix rather than
 * done, because it changes a shipped contract for one word of copy. */
const TOP_RULES_COUNTED = 3;

/* The window the label names before the response arrives.
 *
 * Only used while pending or failed — a successful response carries `window_hours` and that is what
 * gets rendered, so the two cannot disagree about a figure that is on screen. This is the label for a
 * card with no figure yet, and leaving the period out of it would make the heading change shape when
 * the data lands. */
const DEFAULT_FAILURE_HOURS = 24;

/* The chain's last few entries, as the reference's "Recently changed".
 *
 * **Backed by the audit chain, which is the only source that records changes across every module.**
 * The alternative was one read per module merged in the browser, and a feed whose completeness
 * depended on how many modules somebody remembered to include is worse than no feed — it looks
 * complete and is not.
 *
 * **The sentence names the action and the kind of thing, never what changed.** The listing route
 * deliberately does not select the snapshot, so no payload leaves PostgreSQL on this path; that is
 * why this reads "rule.activated on rules.rule" where the reference art reads "Rule EMI amount is
 * positive activated". Naming the rule needs the snapshot, and the honest version of a feed that
 * cannot have it is one that does not pretend to.
 */
/* One source's row on the unmeasured card: name, bar, count — three cells on one line.
 *
 * **A `Fragment` rather than a row element, because the three cells belong to the card's grid.** The
 * bars have to share a left edge to be comparable at all, and a per-row container cannot give them
 * one without either `subgrid` or a hardcoded name width; contributing the cells directly to
 * `.lq-unmeasured` lets one `grid-template-columns` size the name track for every row at once. See
 * that rule for why `display: contents` on a wrapper was not the answer.
 *
 * **Four renderings, because the model draws four readings and a bar can only carry one of them.**
 * The three countless readings get the hatched track `ScoreBar` uses for an unmeasured dimension —
 * an empty track and a measured zero are the same picture, and here "we could not ask scoring" and
 * "everything is scored" would be that same picture *plus* a third meaning. The count column says
 * which in words, and the track's `title` says why.
 *
 * The bar is `--trend-line`, the dark teal this design system already reserves for a figure that
 * carries no verdict. Deliberately not a status hue: an unmeasured dataset is not failing, which is
 * the sentence `home.kpi.measuredGap` already spends a clause on.
 */
function UnmeasuredSource({ row }: { readonly row: UnmeasuredRow }): ReactElement {
  const { t } = useTranslation();

  return (
    <>
      {/* Never the raw id — the bare-UUID defect this codebase records four times. Each absent-name
          case says what is actually true, through the same copy the catalogue's own grouping uses. */}
      <span
        className="lq-mono lq-unmeasured__name"
        /* The three absent-name cases are *sentences* rather than identifiers, and the name column is
           sized for a source name — so they ellipsise, and the full sentence is on `title`. That is
           `.lq-cell__truncate`'s answer reused rather than a second one, and it is affordable here
           only because the count cell beside it already states the reading in words: a reader who
           never hovers still learns that this row was not counted, which is the load-bearing half. */
        title={
          row.name.kind === 'named' ? undefined : t(`catalog.group.${row.name.kind}`)
        }
      >
        {row.name.kind === 'named' ? (
          <Link to={`${SCREEN_PATHS.connections}/${row.connectionId}`}>
            {row.name.name}
          </Link>
        ) : (
          <span className="lq-dim">{t(`catalog.group.${row.name.kind}`)}</span>
        )}
      </span>
      <span
        className={
          row.reading.kind === 'unmeasured'
            ? 'lq-unmeasured__track lq-unmeasured__track--measured'
            : 'lq-unmeasured__track lq-scorebar__track--absent'
        }
        title={
          row.reading.kind === 'allScored'
            ? t('home.unmeasured.allScoredWhy', { total: row.reading.datasets })
            : row.reading.kind === 'unmeasured'
              ? undefined
              : t(`home.unmeasured.${row.reading.kind}Why`)
        }
      >
        {row.reading.kind === 'unmeasured' && row.share !== null ? (
          <span
            className="lq-unmeasured__fill"
            style={{ width: `${String(Math.min(100, row.share * 100))}%` }}
            role="img"
            aria-label={t('home.unmeasured.bar', {
              count: row.reading.count,
              total: row.reading.datasets,
            })}
          />
        ) : null}
      </span>
      <span className="lq-unmeasured__count">
        {row.reading.kind === 'unmeasured'
          ? t('home.unmeasured.count', { count: row.reading.count })
          : t(`home.unmeasured.${row.reading.kind}`)}
      </span>
    </>
  );
}

/* Which sources are holding the estate's unmeasured tables.
 *
 * **Two bounded reads and no fan-out**, both already cached by other screens: the per-source counts
 * come from `GET /v1/catalog/connections/summary` — one statement the database aggregates — and the
 * names from the connection listing every screen that owes a reader a source name already shares. So
 * this section adds no new API surface, which is the constraint the whole page is built under.
 *
 * **A failed read says the read failed and renders no bars.** The two reads fail independently and
 * are handled that way: without the counts there is nothing to draw, so the section is unavailable;
 * without the *names* every count is still true and worth showing, so the rows render with a sentence
 * where the name would be. Letting a naming failure blank a card about coverage would be one read
 * deciding another's availability.
 */
function UnmeasuredBySource(): ReactElement {
  const { t } = useTranslation();
  const summaries = useConnectionSummaries();
  const names = useConnectionNames();

  if (summaries.isPending)
    return <LoadingState rows={3} label={t('home.unmeasured.loading')} />;
  if (summaries.isError) {
    return (
      <UnavailableState
        title={t('home.unmeasured.unreadable')}
        guidance={t('home.unmeasured.unreadableHint')}
      />
    );
  }
  if (summaries.data.items.length === 0) {
    return (
      <EmptyState
        title={t('home.unmeasured.noSources')}
        guidance={t('home.unmeasured.noSourcesGuidance')}
      />
    );
  }

  const rows = unmeasuredBySource(summaries.data.items, {
    status: names.isPending ? 'loading' : names.isError ? 'unreadable' : 'ready',
    /* Empty rather than a guess when the read has not succeeded. `status` is what the model reads to
     * decide which of the three absent-name sentences applies, so an empty map here can never be
     * mistaken for "no connections exist". */
    names: names.isSuccess ? names.data : new Map<string, string>(),
  });
  const total = unmeasuredTotal(summaries.data.items);

  return (
    <Card>
      {/* One grid for every row's cells, so the bars share a left and a right edge. `role="list"` is
          not set and should not be: the grid's children are cells rather than list items, and the
          count each row states in words is what carries the row's meaning to a screen reader. */}
      <div className="lq-unmeasured">
        {rows.map((row) => (
          <UnmeasuredSource key={row.connectionId} row={row} />
        ))}
      </div>
      {/* The sentence the reference puts under the bars, and it is derived from the same rows so the
          card cannot contradict itself — see `unmeasuredTotal` for why it does not quote the estate
          total the KPI above computes from two other reads.

          **Three messages composed rather than one sentence with two empty branches**, which is the
          copy rule and is load-bearing on both of the clauses that can be absent. A partial clause
          inside the count message ships "105 of 517 datasets have never been scored, and 0 sources
          were not counted"; and the *why* clause — profiling runs when somebody asks — is a caveat
          about how a source comes to be unmeasured, which contributes nothing to "everything here is
          scored" and reads as a non-sequitur after it. */}
      <p className="lq-muted lq-table__footnote">
        {t('home.unmeasured.footnote', {
          unmeasured: total.unmeasured,
          datasets: total.datasets,
        })}
        {total.unmeasured > 0 ? ` ${t('home.unmeasured.footnoteWhy')}` : ''}
        {total.notConsulted > 0
          ? ` ${t('home.unmeasured.footnotePartial', { count: total.notConsulted })}`
          : ''}
      </p>
    </Card>
  );
}

function RecentlyChanged({ now }: { readonly now: Date }): ReactElement {
  const { t } = useTranslation();
  const activity = useRecentActivity();

  if (activity.isPending)
    return <LoadingState rows={4} label={t('home.activity.loading')} />;
  if (activity.isError) {
    return (
      <UnavailableState
        title={t('home.activity.unreadable')}
        guidance={activity.error.message}
      />
    );
  }
  if (activity.data.items.length === 0) {
    return (
      <EmptyState
        title={t('home.activity.none')}
        guidance={t('home.activity.noneGuidance')}
      />
    );
  }

  return (
    <Card>
      <ul className="lq-activity">
        {activity.data.items.map((entry) => {
          const when = activityWhen(entry.occurred_at, now);
          const sentence = activitySentence(entry);
          return (
            <li key={activityKey(entry)} className="lq-activity__row">
              <span className="lq-activity__when lumiq-numeric">
                {/* An unparseable timestamp gets a dash rather than "Invalid Date". The contract
                    types this as a date-time, so this branch is defence against a shape the API
                    forbids — which is what a guard is for. */}
                {when === null
                  ? '—'
                  : when.kind === 'time'
                    ? when.at.toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                        /* 24-hour, as the reference shows and as an operator log should be. The
                           locale default gives "01:33 PM" in en-US, which is three characters wider
                           and ambiguous at a glance about ordering — and this column is read by
                           scanning down it. */
                        hourCycle: 'h23',
                      })
                    : t('home.activity.daysAgo', { count: when.days })}
              </span>
              <span className="lq-activity__what">
                {/* **A sentence when the platform has copy for the action, the wire identifier
                    when it does not — and never a blank line or a dropped row.** See
                    `activitySentence`: an unrecognised action is the normal consequence of
                    twenty-one modules writing to one chain, and skipping it would make the feed's
                    completeness unverifiable. The unrecognised form keeps the monospace face and
                    carries a title saying why, so a reader can tell "this is an identifier" from
                    "this is prose". */}
                {sentence.kind === 'sentence' ? (
                  t(sentence.key)
                ) : (
                  <span className="lq-mono" title={t('home.activity.unrecognisedTitle')}>
                    {t(UNRECOGNISED_KEY, {
                      action: sentence.action,
                      kind: sentence.aggregateType,
                    })}
                  </span>
                )}
                {' · '}
                {/* A `system:` actor is rendered differently from a person, matching the audit
                    screen: "who did this" and "a timer did this" are different answers, and a
                    reader chasing an unexpected change needs to know which. */}
                <span
                  className={
                    isSystemActor(entry.actor) ? 'lq-muted' : 'lq-activity__actor'
                  }
                >
                  {entry.actor}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/* The estate's curve as the reference's sparkline.
 *
 * **A hand-drawn SVG polyline rather than a charting library**, for the reason `navIcons` gives: a
 * chart library is a dependency whose licence and air-gap story would need checking, and this is one
 * polyline. Nothing here would be easier with 60 KB of JavaScript.
 *
 * **The y-axis is the observed range, not 0-100, and the caption says so.** A thirty-day line between
 * 61 and 68 plotted against the full scale is a flat line — it would report "nothing changed" about a
 * seven-point move. Plotted against its own range the shape is legible, which is what a sparkline is
 * for, and the two figures beside it are what stop that being misleading: a reader who can see the
 * low and the high knows how much vertical distance means.
 *
 * **A single point draws no line and says so.** Two points are the minimum for a direction, and a
 * one-point "trend" rendered as a dot in the middle of an empty card is a chart claiming to be a
 * measurement of change.
 */
function ScoreTrend(): ReactElement {
  const { t } = useTranslation();
  const trend = useEstateTrend();

  if (trend.isPending) return <LoadingState rows={3} label={t('home.trend.loading')} />;
  if (trend.isError) {
    return (
      <UnavailableState
        title={t('home.trend.unreadable')}
        guidance={trend.error.message}
      />
    );
  }

  const points = trend.data.items;
  if (points.length < 2) {
    return (
      <EmptyState
        title={t('home.trend.tooShort')}
        guidance={t('home.trend.tooShortGuidance', { days: ESTATE_TREND_DAYS })}
      />
    );
  }

  const values = points.map((point) => point.value);
  const low = Math.min(...values);
  const high = Math.max(...values);

  const first = points[0];
  const last = points[points.length - 1];

  return (
    <Card>
      {/* **The arithmetic moved to `Sparkline` and this panel is what proves the move.** It worked
          out the range scaling, the flat-series guard and the non-scaling stroke inline; the KPI row
          needed the same shape, and a second implementation would have been two answers to "how is a
          flat series drawn". Rendering the panel through the shared component is what shows the
          extraction preserved its behaviour rather than merely compiling — see `Spark.tsx`. */}
      <Sparkline
        size="panel"
        points={points.map((point) => ({ value: point.value }))}
        label={t('home.trend.label', {
          days: points.length,
          low: low.toFixed(1),
          high: high.toFixed(1),
        })}
      />
      <div className="lq-spark__axis">
        {/* The dates from the data, never a computed "30 days ago": the series starts where
            measurement started, which is often later than the window asked for. */}
        <span className="lumiq-numeric">{first === undefined ? '' : first.day}</span>
        <span className="lq-muted">
          {t('home.trend.range', { low: low.toFixed(1), high: high.toFixed(1) })}
        </span>
        <span className="lumiq-numeric">{last === undefined ? '' : last.day}</span>
      </div>
    </Card>
  );
}

export function HomeScreen(): ReactElement {
  const { t } = useTranslation();

  const incidents = useIncidentQueue();
  const anomalies = useFindings();
  const review = useReviewQueue();
  const plans = usePlanQueue();
  /* One aggregate rather than a fan-out. See the estate card below. */
  const estate = useEstate();
  /* **The same query the trend panel below reads, and that costs nothing.** React Query dedupes on
     the key, so this is one request shared by both consumers rather than two — which is why reading
     it here is preferable to threading the series down through props from a component that happens
     to already have it. */
  const trendSeries = useEstateTrend();
  const failures = useFailureWindow();
  /* Shared with every other screen that turns a dataset id into a name, so the dashboard cannot
   * disagree with the catalogue about what a table is called. */
  const names = useDatasetNames();
  /* The same query the shell's own chip reads, so this costs no request: React Query dedupes on the
     key. Read here rather than threaded down, because the page header is what needs the name. */
  const identity = useIdentity();

  /* **`now` is the moment the queue was fetched, not the moment of this render**, and the
   * distinction is what makes the ages honest rather than merely stable.
   *
   * A fresh `new Date()` per render would give every age string a new value, so the table would
   * re-render on any unrelated state change and an age could tick over mid-interaction. Pinning it
   * to a `useMemo` with `dataUpdatedAt` as a *dependency* would fix that and still be a small lie —
   * the callback would not read the timestamp, so the age would be measured from whenever the memo
   * happened to run. ESLint caught exactly that as an unnecessary dependency, and it was right.
   *
   * Reading `dataUpdatedAt` as the value makes the dependency real and the claim exact: these ages
   * are as of the last successful read, which is what the reader is looking at. */
  const now = useMemo(() => new Date(incidents.dataUpdatedAt), [incidents.dataUpdatedAt]);

  const severities = incidents.isSuccess ? countSeverities(incidents.data) : null;

  /* The merged worklist. Built here from four reads and ordered in the model, so the ordering is
   * testable without a browser and two cards cannot disagree about what is worst. */
  const items: AttentionItem[] = [];
  if (incidents.isSuccess) {
    for (const incident of incidents.data) {
      if (incident.severity === 'info') continue;
      const naming = nameSubject(
        incident.kind,
        incident.subject_id,
        names.isSuccess ? names.data : null,
      );
      const age = describeAge(ageInMinutes(incident, now));
      items.push({
        id: `incident:${incident.id}`,
        kind: 'incident',
        /* **Never the raw id.** A name when the catalogue can supply one, and otherwise a sentence
         * naming *why* it cannot — which is a different sentence for a contract-breach subject, an
         * unreadable catalogue, a truncated walk and a genuinely unlisted dataset. See
         * `nameSubject`. */
        subject: naming.kind === 'named' ? naming.name : t(naming.reasonKey),
        named: naming.kind === 'named',
        /* The API's own sentence, not a category. `summary` is built from column names and counts
         * and never carries a value from the dataset — see `Incident.summary`. */
        why: incident.summary,
        since: t(`home.attention.age.${age.unit}`, { count: age.count }),
        rank: severityRank(incident.severity),
        tone: incident.severity === 'critical' ? 'danger' : 'warn',
      });
    }
  }
  if (anomalies.isSuccess) {
    for (const finding of anomalies.data) {
      if (finding.verdict !== 'anomalous') continue;
      const naming = nameSubject(
        /* An anomaly finding's subject is always a dataset, so it is named through the same path
         * rather than a second one — passing a monitor kind is how that is said. */
        'volume_anomaly',
        finding.dataset_id,
        names.isSuccess ? names.data : null,
      );
      items.push({
        id: `anomaly:${finding.id}`,
        kind: 'anomaly',
        subject: naming.kind === 'named' ? naming.name : t(naming.reasonKey),
        named: naming.kind === 'named',
        why: t('home.attention.whyAnomaly', { signal: finding.signal }),
        since: t('home.attention.age.unknown'),
        rank: 0,
        tone: 'warn',
      });
    }
  }
  if (review.isSuccess && review.data.length > 0) {
    /* One row for the queue rather than one per dataset. A dashboard's worklist is a set of
     * *decisions*, and "review 40 classifications" is one decision a steward makes once; forty rows
     * would bury every incident beneath a single afternoon's work. */
    items.push({
      id: 'review:queue',
      kind: 'review',
      subject: t('home.attention.reviewSubject', { count: review.data.length }),
      named: true,
      why: t('home.attention.whyReview'),
      since: t('home.attention.age.unknown'),
      rank: 0,
      tone: 'neutral',
    });
  }
  if (plans.isSuccess && plans.data.length > 0) {
    items.push({
      id: 'approval:queue',
      kind: 'approval',
      subject: t('home.attention.approvalSubject', { count: plans.data.length }),
      named: true,
      why: t('home.attention.whyApproval'),
      since: t('home.attention.age.unknown'),
      rank: 0,
      tone: 'neutral',
    });
  }

  const blind = unreadableSources([
    { failed: incidents.isError, label: t('home.attention.sourceIncidents') },
    { failed: anomalies.isError, label: t('home.attention.sourceAnomalies') },
    { failed: review.isError, label: t('home.attention.sourceReview') },
    { failed: plans.isError, label: t('home.attention.sourceApprovals') },
  ]);

  const rows: readonly AttentionRow[] = orderAttention(items).map((item) => ({
    id: item.id,
    /* **The body face, not mono, which is what the reference draws for this column.**
     *
     * The rule this replaces was "mono only for a real identifier", and the distinction it protected
     * is kept: a *named* subject reads at full ink and medium weight, an unnamed one stays muted, so
     * a sentence explaining why the name is missing still cannot be mistaken for the name. What
     * changes is the face. The reference sets `core-banking-pg` and `retail.transactions` in the
     * body face here, and it is right for this table specifically: these are the first words of a
     * worklist row, read as *names* of things to go and look at, where the mono face is for
     * identifiers somebody compares character by character. The catalogue tables keep mono. */
    what: item.named ? (
      <span className="lq-attention__subject">{item.subject}</span>
    ) : (
      <span className="lq-muted">{item.subject}</span>
    ),
    why: item.why,
    since: item.since,
    to: TARGET[item.kind],
    action: t(`home.attention.act.${item.kind}`),
    tone: item.tone,
  }));

  const settled =
    !incidents.isPending && !anomalies.isPending && !review.isPending && !plans.isPending;

  /* When the headline figure actually arrived, as an age. Derived from the *estate* read rather than
     from the newest of the six, because that is the number the page is named for — and quoting the
     freshest of several reads would date the page by whichever happened to refetch last. */
  const asOf = describeAge(
    estate.isSuccess
      ? Math.max(0, Math.floor((Date.now() - estate.dataUpdatedAt) / 60_000))
      : null,
  );

  return (
    <Sections>
      <PageHeader
        title={t('home.title')}
        lede={t('home.lede')}
        /* **How old the figures are, and deliberately not which tenant.**
         *
         * The reference draws a tenant slug here and this platform will not supply one:
         * `_identity_json` omits `tenant_id` from `GET /v1/auth/me` on hard rule 1 — the tenant comes
         * from a validated claim at the edge and is never a value a client holds, because a wire type
         * that declares it is an invitation to plumb it back into a request. A test guards that.
         * Rendering an invented slug would be fake data and reversing the decision would cost the
         * guarantee, so the line is absent and the reason is written down.
         *
         * The freshness half is real and is worth more than it looks. Every figure on this page is a
         * cached read, so "87.4" can be twenty minutes old on a screen somebody is about to act on.
         * `dataUpdatedAt` is when the response actually landed — not a render clock — so it cannot
         * claim to be fresher than the data is. Absent until the read succeeds: a freshness stamp on
         * a failed read would date figures that are not on screen. */
        meta={
          <>
            {/* **The tenant's own name, which `GET /v1/auth/me` now carries.** The note that stood
                here said this line could not be drawn without inventing a slug, and that was right
                about the *id* and wrong about the name: `tenant_id` stays absent on hard rule 1
                because it is a routing key a client could plumb back into a request, and a name
                addresses nothing. `null` renders nothing rather than a placeholder — an OIDC
                deployment can present a valid claim for a tenant with no registry row, and a
                header that invented a name there would be the fake data the id case avoided. */}
            {identity.data?.organisation_name == null ? null : (
              <span>
                {t('home.tenant')} <strong>{identity.data.organisation_name}</strong>
              </span>
            )}
            {estate.isSuccess ? (
              <span>
                {t('home.asOf')}{' '}
                <strong>
                  {t(`home.attention.age.${asOf.unit}`, { count: asOf.count })}
                </strong>
              </span>
            ) : null}
          </>
        }
      />

      {/* ---------------------------------------------------------------- KPI row */}
      <div className="lq-kpi">
        {/* ---- Slot one: the estate's own score, which finally has an endpoint behind it.
             `GET /v1/scoring/estate` aggregates server-side, so this is one request whatever the
             size of the estate — where the only way to build it from the rest of the API was one
             request per dataset. The `BackendBadge` that stood here for months is gone. */}
        {estate.isPending ? (
          <MetricCard
            label={t('home.kpi.estate')}
            value={null}
            absent={t('home.reading')}
            accent="quality"
          />
        ) : estate.isError ? (
          <MetricCard
            label={t('home.kpi.estate')}
            value={null}
            absent={t('home.unreadable')}
            caption={estate.error.message}
            accent="quality"
          />
        ) : estate.data.value === null ? (
          /* Scored nothing yet. **Its own branch rather than a zero**, which is the whole reason the
             API returns `null` here: zero is "everything failed" and this is "nobody has looked",
             and the guidance a reader needs is different for each. */
          <MetricCard
            label={t('home.kpi.estate')}
            value={null}
            absent={t('home.kpi.estateUnscored')}
            caption={<Link to={SCREEN_PATHS.scores}>{t('home.trust.link')}</Link>}
            accent="quality"
          />
        ) : (
          <MetricCard
            label={t('home.kpi.estate')}
            value={estate.data.value.toFixed(1)}
            /* **The trend as a line rather than the score as a bar**, which is the reference and is
               the better restatement: a 0-100 bar under an 87.4 says only "high", where the shape of
               the last thirty days says whether it is moving — and the caption already carries the
               delta as a number. Falls back to the bar when the series has not arrived or holds
               fewer than two points, so a card with one measurement still restates its figure
               instead of showing nothing. */
            visual={
              trendSeries.isSuccess && trendSeries.data.items.length >= 2 ? (
                <Sparkline
                  points={trendSeries.data.items.map((point) => ({ value: point.value }))}
                  label={t('home.kpi.estateSpark', { days: ESTATE_TREND_DAYS })}
                />
              ) : undefined
            }
            bar={{
              percent: estate.data.value,
              label: t('home.kpi.estateBar', { score: estate.data.value.toFixed(1) }),
            }}
            caption={
              <>
                {t('home.kpi.estateAcross', { count: estate.data.scored })}
                {/* The delta only when there is a baseline. `delta === null` is "no earlier
                    measurement", and rendering it as a flat 0.0 would report stability the platform
                    cannot vouch for — so the sentence simply stops. */}
                {estate.data.delta === null ? null : (
                  <>
                    {' · '}
                    <span
                      className={
                        estate.data.delta >= 0
                          ? 'lq-delta lq-delta--up'
                          : 'lq-delta lq-delta--down'
                      }
                    >
                      {/* A glyph as well as a hue and a sign, so the direction survives greyscale —
                          the same rule every status in this product follows. */}
                      {estate.data.delta >= 0 ? '▲' : '▼'}{' '}
                      {t('home.kpi.estateDelta', {
                        delta: Math.abs(estate.data.delta).toFixed(1),
                        days: estate.data.window_days,
                      })}
                    </span>
                  </>
                )}
              </>
            }
            accent="quality"
          />
        )}

        {/* ---- Slot two: how much of the estate is measured at all.
             The reference's "412 of 517", and the two halves come from two places on purpose:
             `scored` from the scoring aggregate, the total from the catalogue walk every other
             screen already does. The scoring API deliberately does not report a dataset total,
             because that count belongs to `core.catalog`.

             The caption says **unmeasured, not failing** in so many words. That is the single
             sentence this card exists for: 105 datasets with no score are a coverage gap somebody
             has to act on, and a reader who took them for zeros would see an estate half broken. */}
        {estate.isSuccess && names.isSuccess ? (
          /* **Clamped to the catalogue, because the two halves come from different populations.**
             `scored` counts rows in `scoring.score`, which survive a dataset being retired — a score
             is a historical fact and erasing it would be wrong. The total counts *live* datasets. So
             a tenant that retired its measured tables read "3 of 0", a card whose numerator exceeded
             its denominator and whose caption then said "every dataset has been scored".

             Clamping is the honest reconciliation rather than a cosmetic one: it cannot claim more
             datasets are measured than exist, and where the catalogue is empty it says so in words
             instead of reporting figures about tables nobody tracks. What it deliberately does *not*
             do is pretend the scores are gone — see the estate card, which still reports the average
             over what was scored, and the note below. */
          names.data.byId.size === 0 ? (
            <MetricCard
              label={t('home.kpi.measured')}
              value={null}
              absent={t('home.kpi.measuredNoCatalogue')}
              caption={t('home.kpi.measuredNoCatalogueWhy')}
              accent="quality"
            />
          ) : (
            <MetricCard
              label={t('home.kpi.measured')}
              value={Math.min(estate.data.scored, names.data.byId.size).toLocaleString()}
              unit={t('home.kpi.measuredOf', { total: names.data.byId.size })}
              /* **The unmeasured tail is hatched, not tinted**, which is this card's whole argument
                 drawn rather than only written: a paler band beside the filled one reads as a second,
                 worse measurement, and 105 datasets with no score are not a bad score. Hatching reads
                 as "no reading here". See `SegmentBar`'s `hatchRemainder` for why the severity card
                 below deliberately does not use it. */
              visual={
                <SegmentBar
                  hatchRemainder
                  segments={[
                    {
                      id: 'scored',
                      share:
                        Math.min(estate.data.scored, names.data.byId.size) /
                        names.data.byId.size,
                      colour: 'var(--status-ok)',
                    },
                  ]}
                  label={t('home.kpi.measuredBar', {
                    scored: Math.min(estate.data.scored, names.data.byId.size),
                    total: names.data.byId.size,
                  })}
                />
              }
              caption={t('home.kpi.measuredGap', {
                count: Math.max(
                  0,
                  names.data.byId.size -
                    Math.min(estate.data.scored, names.data.byId.size),
                ),
              })}
              accent="quality"
            />
          )
        ) : (
          <MetricCard
            label={t('home.kpi.measured')}
            value={null}
            absent={
              estate.isPending || names.isPending
                ? t('home.reading')
                : t('home.unreadable')
            }
            accent="quality"
          />
        )}

        {incidents.isPending ? (
          <MetricCard
            label={t('home.attention.open')}
            value={null}
            absent={t('home.reading')}
          />
        ) : incidents.isError || severities === null ? (
          <MetricCard
            label={t('home.attention.open')}
            value={null}
            absent={t('home.unreadable')}
            caption={t('home.attention.unreadable')}
          />
        ) : (
          <MetricCard
            label={t('home.attention.open')}
            value={needsAttention(severities).toLocaleString()}
            /* Red when a critical incident is open, the accent otherwise. The reference colours this
               card's rule red in its own screenshot, and the reason is the state rather than the
               subject: a card whose rule is permanently red teaches a reader to ignore it. */
            accent={severities.critical > 0 ? 'security' : 'quality'}
            /* **The severity split, and no hatched remainder**, because these three bands *are* the
               whole: every open incident is critical, warning or info, so an unfilled tail would be
               a fourth category nobody named. Info is drawn even though it is excluded from the
               headline — the bar shows the shape of the queue, and the headline counts what needs a
               person, which is the distinction `needsAttention` exists for.

               Shares are of the total the bar draws rather than of the headline, so the three bands
               sum to the track. Guarded on a non-zero total: three zero-share bands would render as
               an empty pill, which is a picture of nothing beside a figure of zero. */
            visual={
              severities.critical + severities.warning + severities.info > 0 ? (
                <SegmentBar
                  segments={[
                    {
                      id: 'critical',
                      share:
                        severities.critical /
                        (severities.critical + severities.warning + severities.info),
                      colour: 'var(--status-error)',
                    },
                    {
                      id: 'warning',
                      share:
                        severities.warning /
                        (severities.critical + severities.warning + severities.info),
                      colour: 'var(--status-warn)',
                    },
                    {
                      id: 'info',
                      share:
                        severities.info /
                        (severities.critical + severities.warning + severities.info),
                      colour: 'var(--status-unknown)',
                    },
                  ]}
                  label={t('home.attention.severityBar', {
                    critical: severities.critical,
                    warning: severities.warning,
                    info: severities.info,
                  })}
                />
              ) : undefined
            }
            caption={
              /* The breakdown under the headline, because "4" is not actionable and
                 "2 critical · 3 warnings · 2 informational" is.

                 **All three severities, where this listed two.** The bar above already draws info,
                 and a caption that named only the two the headline counts left the third band in the
                 picture unexplained — a colour in a graphic with no key. Info is still excluded from
                 the *headline*, which is what `needsAttention` exists for: the figure counts what
                 wants a person and the caption describes the shape of the queue.

                 Dot-separated text rather than three `Indicator`s. See `.lq-kpi__split` for what that
                 gives up and why it costs nothing here. */
              <span className="lq-kpi__split">
                {/* Emphasised only when there is something to emphasise — a red "no critical" would
                    paint the good news as bad news, which is the same asymmetry `.lq-delta` draws
                    between a rise and a fall. */}
                <span className={severities.critical > 0 ? 'lq-kpi__lead' : 'lq-muted'}>
                  {t('home.attention.critical', { count: severities.critical })}
                </span>
                <span className="lq-muted">
                  {t('home.attention.warning', { count: severities.warning })}
                </span>
                <span className="lq-muted">
                  {t('home.attention.info', { count: severities.info })}
                </span>
              </span>
            }
          />
        )}

        {/* ---- Slot four: how much broke in the last day.
             **Anomalies and the review queue used to be here and have moved out**, not been
             dropped: both are already rows in the worklist immediately below, so the KPI row was
             restating them — and a dashboard that says the same thing twice makes a reader check
             whether the two figures agree instead of acting on either.

             The concentration sentence is the reason this card is worth its slot. "23 failures" is
             a number; "23 across 9 datasets, and 3 rules account for 19" tells a steward whether
             the afternoon is three rule fixes or nine investigations. `concentrated` comes from the
             server rather than being derived here, because the claim is only true when there are
             more rules than the top-N covers, and a client deciding that itself gets it wrong on a
             tenant with two rules. */}
        {failures.isPending ? (
          <MetricCard
            label={t('home.kpi.failures', { hours: DEFAULT_FAILURE_HOURS })}
            value={null}
            absent={t('home.reading')}
            accent="quality"
          />
        ) : failures.isError ? (
          <MetricCard
            label={t('home.kpi.failures', { hours: DEFAULT_FAILURE_HOURS })}
            value={null}
            absent={t('home.unreadable')}
            caption={failures.error.message}
            accent="quality"
          />
        ) : (
          <MetricCard
            label={t('home.kpi.failures', { hours: failures.data.window_hours })}
            value={failures.data.violations.toLocaleString()}
            /* **Concentration, not a per-day bar chart — and the difference is a real constraint
               rather than a simplification.** The reference draws eight bars here, which needs a
               daily or per-rule series, and `FailureWindow` returns six scalars: violations,
               datasets, rules, the top-N sum and whether it is concentrated. There is no endpoint
               that reports the series, so eight bars would be eight invented numbers on the one
               screen a steward trusts most.

               What the two figures the server *does* return draw honestly is the sentence the caption
               already makes: how much of the day's damage is three rules. That is the actionable
               shape — a wide red band means three fixes, a narrow one means nine investigations — and
               it is the same claim in a picture rather than a different one.

               Guarded on `concentrated`, which is the server's own judgement: on a tenant with two
               rules the top-N sum is the whole and a bar showing 100% would say something true and
               useless. */
            visual={
              failures.data.violations > 0 && failures.data.concentrated ? (
                <SegmentBar
                  segments={[
                    {
                      id: 'top',
                      share: failures.data.top_rule_violations / failures.data.violations,
                      colour: 'var(--status-error)',
                    },
                    {
                      id: 'rest',
                      share:
                        Math.max(
                          0,
                          failures.data.violations - failures.data.top_rule_violations,
                        ) / failures.data.violations,
                      colour: 'var(--status-unknown)',
                    },
                  ]}
                  label={t('home.kpi.failuresBar', {
                    share: failures.data.top_rule_violations,
                    total: failures.data.violations,
                    rules: TOP_RULES_COUNTED,
                  })}
                />
              ) : undefined
            }
            caption={
              failures.data.violations === 0 ? (
                /* Zero is a real answer and gets its own sentence rather than the concentration
                   line with zeros in it, which would read as a broken template. */
                t('home.kpi.failuresNone')
              ) : (
                <Link to={SCREEN_PATHS.executions}>
                  {failures.data.concentrated
                    ? t('home.kpi.failuresConcentrated', {
                        datasets: failures.data.datasets,
                        rules: TOP_RULES_COUNTED,
                        share: failures.data.top_rule_violations,
                      })
                    : t('home.kpi.failuresSpread', { datasets: failures.data.datasets })}
                </Link>
              )
            }
            /* Red when anything broke: this is the one card on the row whose subject is a *fault*
               rather than a measurement, and the reference colours its top rule accordingly. */
            accent={failures.data.violations > 0 ? 'security' : 'quality'}
          />
        )}
      </div>

      {/* ---------------------------------------------------------------- needs attention */}
      {/* No hint. It read "a zero here is an answer, not an absence" — which is this platform's
          central rule and exactly the wrong place for it: the sentence matters when the table *is*
          empty, and that is what the table's own empty state says. Sitting above a table with four
          rows in it, it was a caveat about a case the reader was not looking at. */}
      <Section title={t('home.attention.title')}>
        {blind.length > 0 ? (
          /* Named, above the table, because the failure makes the worklist *shorter* while looking
           * complete. A reader must not conclude "less needs me" from a request that failed. */
          <p className="lq-attention__blind">
            {t('home.attention.blind', { sources: blind.join(', ') })}
          </p>
        ) : null}

        {!settled ? (
          <LoadingState rows={3} label={t('home.attention.loading')} />
        ) : (
          /* Inside a `flush` card, which is exactly what `flush` exists for — "a card whose only
             child is a table". The worklist was the one table on this page sitting on the page's own
             ground, so its header row and its separators had nothing to be separators *of*: the
             reference draws it as a white panel with the header inside it, and every other
             table-holding surface in the product is already a card.

             A `box-shadow` rather than the 1px border the art appears to show. That is the product's
             card treatment — a soft shadow reads as a hairline in a flat screenshot — and giving this
             one a border would make it the only card in the product outlined instead of raised. The
             `.lq-kpi` panel is bordered because it is a *structural* division of the page rather than
             a card, which is a different object. */
          <Card flush>
            <AttentionTable rows={rows} empty={t('home.attention.clearGuidance')} />
          </Card>
        )}
      </Section>

      {/* ---------------------------------------------------------------- unmeasured by source

           Between the worklist and the lower half, as the reference has it, and the position is the
           argument for the section: the worklist above is everything the platform has *found*, and
           this is where it has not looked. A steward who clears the queue above has not finished if
           a source has never been scored — that is a gap the queue cannot contain, because an
           unmeasured dataset produces no incident, no anomaly and no finding. */}
      <Section title={t('home.unmeasured.title')}>
        <UnmeasuredBySource />
      </Section>

      {/* ---------------------------------------------------------------- the lower half

           Two sections side by side, as the reference has them. **This replaces "Your estate" and
           "This deployment"**, and neither loses a reader anything: the source count is the
           Connections screen's subject and the degraded-capability count is already the banner at the
           top of every page, which links to the same place. What was missing was any answer to "what
           just happened" and "which way is this going" — the two questions a front page is opened to
           ask, and the two the reference gives its lower half to. */}
      <div className="lq-halves">
        <Section title={t('home.activity.title')}>
          <RecentlyChanged now={now} />
        </Section>
        <Section title={t('home.trend.title', { days: ESTATE_TREND_DAYS })}>
          <ScoreTrend />
        </Section>
      </div>
    </Sections>
  );
}

/* Where each kind of row sends a reader. A map rather than a chain of conditionals so a new
 * `AttentionKind` is a compile error here rather than a row whose action link goes nowhere. */
const TARGET: Readonly<Record<AttentionItem['kind'], string>> = {
  incident: SCREEN_PATHS.incidents,
  anomaly: SCREEN_PATHS.anomalies,
  review: SCREEN_PATHS.review,
  approval: SCREEN_PATHS.remediation,
};
